import {
    type ReactNode
} from "react";

import {
    Link
} from "react-router";

import {
    AdminContentWorkspace
} from "../features/admin/AdminContentWorkspace.js";
import { useAdminText } from "../features/admin/adminI18n.js";

import {
    useAdminAccess
} from "../features/admin/AdminAccessProvider.js";

import {
    useAuth
} from "../services/backend/AuthProvider.js";

import {
    useBackend
} from "../services/backend/BackendProvider.js";

import {
    ErrorState,
    LoadingState
} from "../ui/components/Feedback.js";

import {
    Page,
    PageHeader
} from "../ui/components/Layout.js";

function AdminContentPage() {
    const adminText = useAdminText();
    const {
        status: authStatus,
        user
    } = useAuth();
    const {
        client,
        connectionStatus
    } = useBackend();
    const { status: accessStatus, retry } = useAdminAccess();

    if (connectionStatus === "disabled") {
        return (
            <AdminShell>
                <ErrorState
                    title={adminText("Backend requis")}
                    description={adminText("Le panel éditorial fonctionne uniquement avec Supabase configuré.")}
                />
            </AdminShell>
        );
    }

    if (
        authStatus === "loading"
        || connectionStatus === "connecting"
    ) {
        return (
            <AdminShell>
                <LoadingState label={adminText("Vérification de la session…")} />
            </AdminShell>
        );
    }

    if (
        authStatus !== "signed-in"
        || !user
    ) {
        return (
            <AdminShell>
                <ErrorState
                    title={adminText("Connexion requise")}
                    description={adminText("Connectez-vous avec votre compte administrateur avant d’ouvrir l’atelier éditorial.")}
                    action={
                        <Link
                            className="inline-flex min-h-11 items-center justify-center rounded-control bg-dino-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-dino-700"
                            to="/auth?returnTo=%2Fadmin%2Fcontent"
                        >
                            {adminText("Se connecter")}
                        </Link>
                    }
                />
            </AdminShell>
        );
    }

    if (accessStatus === "unchecked" || accessStatus === "checking") {
        return (
            <AdminShell>
                <LoadingState label={adminText("Vérification des droits éditoriaux…")} />
            </AdminShell>
        );
    }

    if (accessStatus === "denied") {
        return (
            <AdminShell>
                <ErrorState
                    title={adminText("Accès administrateur requis")}
                    description={
                        <>
                            {adminText("Ce compte n’est pas dans l’allowlist éditoriale.")}
                            <span className="mt-2 block break-all font-mono text-xs">
                                {adminText("Identifiant")} : {user.id}
                            </span>
                        </>
                    }
                />
            </AdminShell>
        );
    }

    if (
        accessStatus === "error"
        || !client
    ) {
        return (
            <AdminShell>
                <ErrorState
                    title={adminText("Vérification impossible")}
                    description={adminText("Le serveur n’a pas pu confirmer les droits administrateur.")}
                    retryLabel={adminText("Recommencer")}
                    onRetry={retry}
                />
            </AdminShell>
        );
    }

    return (
        <AdminContentWorkspace client={client} />
    );
}

function AdminShell({
    children
}: {
    children: ReactNode;
}) {
    const adminText = useAdminText();
    return (
        <Page>
            <PageHeader
                eyebrow={adminText("Administration")}
                icon="🦕"
                title={adminText("Atelier des contenus")}
                description={adminText("Créer, réviser et publier le corpus canonique.")}
            />
            {children}
        </Page>
    );
}

export {
    AdminContentPage
};
